<script lang="ts" setup>
  import { computed } from 'vue';
  import './MpfInputText.scss';

  import type { MpfInputTextProps, MpfTextInputEmits } from './MpfInputText.props.ts';
  import { mpfTextInputDefaults } from './MpfInputText.props';

  // NEW
  import MpfLabel from '../MpfLabel/MpfLabel.vue';
  import MpfHint from '../MpfHint/MpfHint.vue';

  const props = withDefaults(defineProps<MpfInputTextProps>(), mpfTextInputDefaults);
  const emit = defineEmits<MpfTextInputEmits>();

  const hasErrors = computed(() => (props.errors?.length ?? 0) > 0);

  const widthClass = computed(() => {
    if (props.width === 'large') return 'mpf-input--width-30';
    if (props.width === 'small') return 'mpf-input--width-10';
    if (typeof props.width === 'number') return `mpf-input--width-${props.width}`;
    return 'mpf-input--width-20';
  });

  const inputClasses = computed(() => [
    'mpf-input',
    widthClass.value,
    hasErrors.value ? 'mpf-input--error' : '',
  ]);

  const groupClasses = computed(() => [
    'mpf-form-group',
    hasErrors.value ? 'mpf-form-group--error' : '',
  ]);

  // ENG-21793: number questions render as a text input with a numeric keypad, per NHS DS
  const inputMode = computed(() => (props.type === 'number' ? 'numeric' : undefined));

  const hintId = computed(() => (props.hint ? `${props.id}-hint` : ''));
  const errorId = computed(() => (hasErrors.value ? `${props.id}-error` : ''));

  const describedBy = computed(() => {
    return [props.ariaDescribedby, hintId.value, errorId.value].filter(Boolean).join(' ').trim();
  });

  // emits
  function onInput(e: Event) {
    let val = (e.target as HTMLInputElement).value;
    if (props.numbersOnly) {
      // allow only 0–9
      val = val.replace(/\D+/g, '');
      (e.target as HTMLInputElement).value = val;
    }

    emit('update:modelValue', val);
    emit('input', e);
  }

  function onChange(e: Event) {
    emit('change', e);
  }

  function onBlur(e: Event) {
    emit('blur', e);
  }

  function onFocus(e: Event) {
    emit('focus', e);
  }
</script>

<template>
  <div :class="groupClasses">
    <template v-if="size === 'large'">
      <MpfLabel
        :for-id="id"
        :label="label"
        :required="required"
        :size="size"
        :aria-describedby="hintId"
        :heading-level-size="headingLevelSize"
      />
    </template>

    <template v-else>
      <MpfLabel
        :for-id="id"
        :label="label"
        :required="required"
        :size
        :aria-describedby="hintId"
        :heading-level-size="headingLevelSize"
      />
    </template>

    <MpfHint v-if="hint" :hint="hint" :sr-hint="srHint" :id="hintId" />

    <!-- ERRORS -->
    <div v-if="hasErrors" :id="errorId" class="mpf-error-message" role="alert" aria-live="polite">
      {{ errors![0] }}
    </div>

    <div class="mpf-input__wrapper">
      <input
        :id="id"
        :name="name || id"
        :class="inputClasses"
        :aria-describedby="describedBy || undefined"
        :required="required || undefined"
        v-bind="$attrs"
        :inputmode="inputMode"
        :value="modelValue"
        @input="onInput"
        @change="onChange"
        @focus="onFocus"
        @blur="onBlur"
        data-private
      />
    </div>
  </div>
</template>
